// Removed duplicate import that conflicted with local declaration

import { AccountPaymentConfig } from '@sdk/shared/types/config.types';

export interface OrderPayload {
  order: PreCreateOrder;
  adId: number;
  signature?: string | null;
  paymentConfig?: number;
}

// Sell flow statuses
export type SellOrderStatus =
  | 'initiated'
  | 'wait_for_payment'
  | 'release_funds'
  | 'completed'
  | 'cancelled'
  | 'appealed'
  | 'expired';

// Buy flow statuses
export type BuyOrderStatus =
  | 'initiated'
  | 'wait_for_seller'
  | 'pay_seller'
  | 'wait_for_release'
  | 'completed'
  | 'cancelled'
  | 'appealed'
  | 'expired';

// Combined type for all possible statuses
export type OrderStatus = SellOrderStatus | BuyOrderStatus;

export interface OrderStep {
  status: OrderStatus;
  title: string;
  description: string;
  timeLimit?: string;
}

export interface OrderDetails {
  id: number;
  advertiser: {
    name: string;
    trades: string;
    completionRate: string;
    ratings: string;
    duration: string;
  };
  price: string;
  currency: string;
  chain: any; // Removed chainOptions dependency
  availability: string;
  cryptoCurrency: string;
  minimum: string;
  maximum: string;
  paymentOptions: string[];
  termsAndCondition: {
    id: number;
    description: string;
  }[];
}

export enum OrderState {
  pending,
  accepted,
  paid,
  appealed,
  released,
  cancelled,
  intent,
}

export type OrderStateKey = keyof typeof OrderState;

export enum OrderType {
  buy,
  sell,
}

export type CreateOrder = {
  trader: `0x${string}`;
  merchant: `0x${string}`;
  traderChain: bigint;
  merchantChain: bigint;
  token: `0x${string}`;
  currency: `0x${string}`;
  paymentMethod: `0x${string}`;
  orderType: OrderType;
  quantity: bigint;
  expiry: bigint;
  duration: bigint;
};

export type PreCreateOrder = {
  trader: `0x${string}`;
  merchant: `0x${string}`;
  traderChain: number;
  merchantChain: number;
  tokenDecimals: number;
  token: `0x${string}`;
  currency: string;
  paymentMethod: string;
  orderType: OrderType;
  quantity: number;
  expiry: number;
  duration: number;
};

export type BackendOrderArgs = PreCreateOrder & {
  paymentConfig: AccountPaymentConfig['id'];
};

export enum OrderMethod {
  createOrder,
  acceptQuickTradeByMerchant,
  acceptOrder,
  payOrder,
  releaseOrder,
  cancelOrder,
  appealOrder,
  cancelAppeal,
  settleAppeal,
}

export type OrderMethodKey = keyof typeof OrderMethod;

export type OrderMethodPayload = {
  orderHash: `0x${string}`;
  method: ContractOrderMethod;
  expiry: number;
};
export type OrderMethodPayloadForContract = {
  orderHash: `0x${string}`;
  method: ContractOrderMethod;
  expiry: bigint;
};

export type UpdateOrder = {
  orderHash: string;
  state: OrderState;
  signature: string;
  expiry: number;
  methodHash?: string;
};

export type OrderStateResponse = {
  id: string;
  state: OrderStateKey;
};

export type Actor = {
  completionRate: number;
  responseTime: number;
  trades: number;
  rating: number;
  address: `0x${string}`;
  account?: { username?: string };
  id: string;
};

export type OrderResponse = {
  id: string;
  state: OrderStateKey;
  quickTradeIndex: string | null;
  price: number;
  type: 'buy' | 'sell';
  quantity: number;
  transactionHash: null | string;
  expiry: number;
  duration: number;
  deadline: string;
  createdAt: string;
  expiresAt: string;
  contract: string;
  traderSignature: `0x${string}` | null;
  merchantSignature: `0x${string}` | null;
  traderNetwork: {
    name: string;
    explorer: string;
    rpc: string;
    contract: `0x${string}`;
    id: number;
    iconUrl: string;
  };
  merchantNetwork: {
    name: string;
    explorer: string;
    rpc: string;
    contract: `0x${string}`;
    id: number;
    iconUrl: string;
  };
  trader: Actor;
  merchant: Actor;
  token: {
    address: `0x${string}`;
    name: string;
    symbol: string;
    id: `0x${string}`;
    decimals: number;
  };
  currency: {
    name: string;
    symbol: string;
    id: `0x${string}`;
  };
  stateHistory:
    | {
        state: OrderStateKey;
        transactionHash: null | string;
        methodHash: null | string;
        stateConfirmendAt: null | string;
        createdAt: string;
        id: string;
      }[]
    | undefined;
  paymentConfig: null | AccountPaymentConfig;
  paymentMethod: {
    id: string;
    name: string;
    color: string;
  };
  ad: {
    id: number;
    message: string;
    tags: string[];
    paymentConfigs: Array<{
      id: number;
      payment: {
        method: {
          name: string;
          color: string;
          status: string;
          id: string;
        };
        address: {
          address: string;
          id: string;
        };
        account: string;
        accountName: string;
        extraInfo: string;
        isActive: boolean;
      };
    }>;
    timeLimit: number;
  };
  orderStats: OrderStats;
  merchantReviews: MerchantReviews;
  merchantStats: MerchantStats;
  indexer: {
    indexer: string;
    order: {
      createdAt: string;
      currency: string;
      deadline: string;
      id: `0x${string}`;
      merchant: `0x${string}`;
      orderHash: `0x${string}`;
      orderState: OrderStateKey;
      orderType: OrderTypeKey;
      paymentMethod: string;
      quantity: string;
      trader: `0x${string}`;
      token: `0x${string}`;
      traderChain: string;
      transaction: `0x${string}`;
    };
  };
};

export type CreateOrderResponse = {
  adId: number;
  contract: `0x${string}`;
  createdAt: string;
  currencyId: string;
  deadline: string;
  duration: number;
  expiresAt: string;
  expiry: number;
  id: string;
  merchantAddress: `0x${string}`;
  merchantNetworkId: number;
  merchantSignature: `0x${string}` | null;
  paymentConfigId: number | null;
  paymentMethodId: string;
  price: number;
  quantity: number;
  state: OrderStateKey;
  tokenId: string;
  traderAddress: `0x${string}`;
  traderNetworkId: number;
  traderSignature: `0x${string}` | null;
  transactionHash: string | null;
  type: OrderTypeKey;
  updatedAt: string;
};

export type OrderTypeKey = keyof typeof OrderType;

export type UpdateOrderPayload = {
  methodPayload: {
    orderHash: `0x${string}`;
    method: number;
    expiry: number;
  };
  //signture over methodPayload
  signature?: `0x${string}`;
  paymentConfig?: number;
};

export type AcceptOrderPayload = {
  methodPayload: `0x${string}`;
  //signture over original order
  signature: `0x${string}`;
  paymentConfig?: number;
};

export type OrderReviewPayload = {
  score: 1 | -1;
  text?: string;
};

export interface Review {
  id: number;
  orderId: string;
  adId: number;
  score: 1 | -1;
  text?: string;
  reviewerId: string;
  revieweeId: string;
  createdAt: string;
  reviewee: {
    account: {
      username: string;
    };
  };
  reviewer: {
    account: {
      username: string;
    };
  };
}

export interface ReviewStats {
  _count: {
    score: number;
  };
  revieweeId: string;
  score: 1 | -1;
}

export interface ReviewsResponse {
  recentReviews: Review[];
  reviewStats: ReviewStats[];
}

export interface MerchantStatsData {
  merchantStats: MerchantStats;
  reviewStats: ReviewStats[];
  reviews: {
    total: number;
    positive: number;
    negative: number;
  };
}

export interface MerchantStats {
  totalOrders: number;
  paid: {
    total: number;
    time: number;
  };
  released: {
    total: number;
    time: number;
  };
  accepted: {
    total: number;
    time: number;
  };
  address: string;
  username: string | null;
  positiveRates?: number;
}

export interface MerchantReviews {
  total: number;
  positive: number;
  negative: number;
}

export interface OrderStats {
  totalOrders: number;
  completionRate: string;
  avgReleaseTime: string;
  avgPayTime: string;
  positiveRates: number;
  timeLimit: number;
}

export type OrdersQuery = {
  state?: string; // for multiple states separate with comma
  limit?: number;
  page?: number;
  token?: string;
  tokenSymbol?: string;
  merchant?: string;
  currency?: string;
  paymentMethod?: string;
  minPrice?: number;
  network?: number;
  minAmount?: number;
  minQuantity?: number;
  expiryBefore?: number; // in milliseconds
  expiryAfter?: number; // in milliseconds
  deadlineBefore?: number; // in milliseconds
  deadlineAfter?: number; // in milliseconds
  startCreatedAt?: number; // in milliseconds
  endCreatedAt?: number; // in milliseconds
  type?: 'buy' | 'sell';
  orderBy?: 'price' | 'quantity' | 'deadline' | 'expiry' | 'createdAt';
  orderDirection?: 'desc' | 'asc';
};

export type OrderUpdate = {
  createdAt: string;
  expiry: number;
  id: string;
  methodHash: `0x${string}`;
  orderId: string;
  signature: `0x${string}`;
  state: OrderStateKey;
  stateConfirmendAt: string | null;
  transactionHash: string | null;
  updatedAt: string;
};

export interface CreateQuickTradePayload {
  trader: `0x${string}`;
  tokenAddress: string;
  currency: string;
  traderChain: number;
  quantity: number;
  priceLimit: number;
  type: 'buy' | 'sell';
  paymentMethod: string;
  paymentConfig?: number;
}

export type acceptQuickTradeByMerchantPayload = {
  quickTradeId: string;
  orderSig: OrderPayload;
};

export interface QuickTrade extends CreateQuickTradePayload {
  id: string;
  orderHash: string; // empty when pending
  status: QuickTradeStatus['status'];
  currentAdId: number | null; // the ad ID currently being processed
  lastContactedTime: number;
}

export type QuickTradeStatus = {
  id: string;
  orderHash: string;
  adId: number;
  status: 'pending' | 'accepted' | 'expired';
  lastContactedTime: number;
};

export type GetQuickRatePayload = {
  tokenAddress: string;
  currency: string;
  traderChain: number;
  quantity: number;
  paymentMethod: string;
  type: 'buy' | 'sell';
  priceLimit?: number;
};

export interface QuickRateResult {
  rate: number | null;
  bestRate?: number;
  worstRate?: number;
  numAds: number;
}

export type RejectQuickTradePayload = {
  adId: string;
};

export enum ContractOrderMethod {
  createOrder,
  payOrder,
  releaseOrder,
  cancelOrder,
  appealOrder,
  acceptOrder,
  cancelAppeal,
  settleAppeal,
}

export type ContractOrderMethodKey = keyof typeof ContractOrderMethod;
